/**
 * FIREBASE CONFIGURATION MODULE - Authentication and Database Services
 * 
 * This module initializes and configures Firebase services for the medical monitoring
 * application. It provides authentication and database functionality for user management
 * and data storage in the cloud.
 * 
 * Key Features:
 * - Firebase Authentication for user login/logout
 * - Firestore database for user data and medical records
 * - Environment-based configuration with fallback
 * - Error handling for missing configuration
 * - Service initialization with proper error handling
 * 
 * Services Provided:
 * - Authentication: User login, logout, registration, email verification
 * - Firestore: Cloud database for user profiles and medical data
 * - Configuration: Environment-based setup with fallback values
 * 
 * Configuration Sources:
 * - Environment variables (preferred for production)
 * - Fallback configuration for development
 * - Error handling for missing configuration
 */

// Firebase core imports for app initialization and service configuration
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';

/**
 * FIREBASE CONFIGURATION SETUP
 * 
 * Configures Firebase services using environment variables with fallback values.
 * This approach ensures the application works in both development and production
 * environments while maintaining security best practices.
 * 
 * Environment Variables (preferred):
 * - REACT_APP_FIREBASE_API_KEY: Firebase API key
 * - REACT_APP_FIREBASE_AUTH_DOMAIN: Authentication domain
 * - REACT_APP_FIREBASE_PROJECT_ID: Firebase project identifier
 * - REACT_APP_FIREBASE_STORAGE_BUCKET: Storage bucket for files
 * - REACT_APP_FIREBASE_MESSAGING_SENDER_ID: Messaging service ID
 * - REACT_APP_FIREBASE_APP_ID: Application identifier
 * 
 * Fallback Configuration:
 * - Uses existing project (untitled-7d33f) if environment variables are missing
 * - Ensures application continues to function in development
 * - Provides default values for testing and development
 */
const envConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,                    // Firebase API key for authentication
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,          // Authentication domain for user login
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,             // Firebase project identifier
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,     // Storage bucket for file uploads
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID, // Messaging service identifier
  appId: process.env.REACT_APP_FIREBASE_APP_ID,                     // Application identifier
} as Record<string, string | undefined>;

const fallbackConfig = {
  apiKey: 'AIzaSyAbhVTGtQK-VXPfVemJ2SvZydQBa6sMU-c',
  authDomain: 'untitled-7d33f.firebaseapp.com',
  projectId: 'untitled-7d33f',
  storageBucket: 'untitled-7d33f.firebasestorage.app',
  messagingSenderId: '79447303355',
  appId: '1:79447303355:web:11b930f7037e5f9addb59e',
};

const hasEnv = !!(envConfig.apiKey && envConfig.projectId && envConfig.appId);
const firebaseConfig = (hasEnv ? envConfig : fallbackConfig) as any;

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;

try {
  // Initialize only if required keys exist to avoid crashing on startup
  if (firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId) {
    app = initializeApp(firebaseConfig as any);
    auth = getAuth(app);
    db = getFirestore(app);
  } else {
    console.warn('Firebase env vars missing. App will run without Firebase until configured.');
  }
} catch (e) {
  console.error('Failed to initialize Firebase:', e);
}

export { auth, db };

export default app;


